import { useState } from "react";
import { ImageIcon, X, Loader2, Upload, ZoomIn, FileText, Eye } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import type { FieldDef } from "@endeks/shared";
import { RichTextEditor } from "./RichTextEditor";
import MediaPicker from "../Media/MediaPicker";
import { useUploadMedia } from "../../features/media/queries";

// Görsel alanı: değer yalnız MinIO URL string'idir. MediaPicker mevcut media
// kütüphanesinden (API -> MinIO) seçim yapar; sürükle-bırak ise bilgisayardan
// bırakılan dosyayı aynı upload API'siyle MinIO'ya yükler. Yeni bir depolama
// yolu açmaz.
function ImageField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  const [zoomOpen, setZoomOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const uploadMutation = useUploadMedia();

  const uploadFile = async (file: File) => {
    if (!file.type.startsWith("image/")) {
      toast.error("Yalnızca görsel dosyaları yüklenebilir");
      return;
    }
    try {
      const media = await uploadMutation.mutateAsync({ file });
      onChange(media.url);
      toast.success("Dosya yüklendi");
    } catch {
      toast.error("Yükleme başarısız");
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (uploadMutation.isPending) return;
    const file = e.dataTransfer.files?.[0];
    if (file) void uploadFile(file);
  };

  return (
    <div className="space-y-2">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          if (!isDragging) setIsDragging(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          setIsDragging(false);
        }}
        onDrop={handleDrop}
        className={cn(
          "relative rounded-lg border-2 border-dashed border-transparent transition-colors",
          isDragging && "border-primary bg-primary/5",
          uploadMutation.isPending && "pointer-events-none opacity-70",
        )}
      >
        {value ? (
          <div className="relative inline-block p-1">
            <button
              type="button"
              onClick={() => setZoomOpen(true)}
              className="group relative block cursor-zoom-in rounded border border-border bg-muted/20"
              aria-label="Görseli büyüt"
            >
              <img src={value} alt="" className="h-24 object-contain rounded" />
              <span className="absolute inset-0 flex items-center justify-center rounded bg-black/0 text-transparent transition-colors group-hover:bg-black/40 group-hover:text-white">
                <ZoomIn className="h-5 w-5" />
              </span>
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center gap-1.5 py-6 text-muted-foreground">
            <Upload className="h-5 w-5" />
            <span className="text-xs">Görseli buraya sürükleyip bırakın</span>
          </div>
        )}
        {(isDragging || uploadMutation.isPending) && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 rounded-lg bg-background/80 text-sm text-primary">
            {uploadMutation.isPending ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                <span className="text-xs">Yükleniyor…</span>
              </>
            ) : (
              <>
                <Upload className="h-5 w-5" />
                <span className="text-xs">Yüklemek için bırakın</span>
              </>
            )}
          </div>
        )}
      </div>
      <div className="flex gap-2">
        <Input
          value={value}
          placeholder="Görsel URL'i (MinIO)"
          onChange={(e) => onChange(e.target.value)}
          className="flex-1"
        />
        <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)}>
          <ImageIcon className="h-4 w-4" />
          Seç
        </Button>
        {value && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onChange("")}
            className="text-destructive hover:text-destructive"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
      <MediaPicker
        defaultFolder="content"
        open={open}
        onClose={() => setOpen(false)}
        onSelect={(url) => {
          onChange(url);
          setOpen(false);
        }}
      />
      <Dialog open={zoomOpen} onOpenChange={setZoomOpen}>
        <DialogContent className="max-w-3xl">
          <DialogTitle className="sr-only">Görsel önizleme</DialogTitle>
          {value && (
            <img
              src={value}
              alt=""
              className="mx-auto max-h-[80vh] w-auto object-contain rounded"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Belge (PDF vb.) alanı: değer yalnız MinIO URL string'idir. Görsel alanından farkı,
// önizlemenin dosya adı olması ve yükleme türünün görsele kısıtlanmamasıdır.
function FileField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const uploadMutation = useUploadMedia();

  const uploadFile = async (file: File) => {
    try {
      const media = await uploadMutation.mutateAsync({ file, folder: "content" });
      onChange(media.url);
      toast.success("Dosya yüklendi");
    } catch {
      toast.error("Yükleme başarısız");
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (uploadMutation.isPending) return;
    const file = e.dataTransfer.files?.[0];
    if (file) void uploadFile(file);
  };

  const filename = value ? (value.split("/").pop() ?? value) : "";

  return (
    <div className="space-y-2">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          if (!isDragging) setIsDragging(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          setIsDragging(false);
        }}
        onDrop={handleDrop}
        className={cn(
          "relative rounded-lg border-2 border-dashed border-transparent transition-colors",
          isDragging && "border-primary bg-primary/5",
          uploadMutation.isPending && "pointer-events-none opacity-70",
        )}
      >
        {value ? (
          <div className="flex items-center gap-2 p-2 rounded border border-border bg-muted/20">
            <FileText className="h-5 w-5 shrink-0 text-muted-foreground" />
            <span className="text-sm truncate flex-1" title={filename}>
              {filename}
            </span>
            <a
              href={value}
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 text-muted-foreground hover:text-foreground"
              title="Görüntüle"
            >
              <Eye className="h-4 w-4" />
            </a>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center gap-1.5 py-6 text-muted-foreground">
            <Upload className="h-5 w-5" />
            <span className="text-xs">Dosyayı buraya sürükleyip bırakın</span>
          </div>
        )}
        {(isDragging || uploadMutation.isPending) && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 rounded-lg bg-background/80 text-sm text-primary">
            {uploadMutation.isPending ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                <span className="text-xs">Yükleniyor…</span>
              </>
            ) : (
              <>
                <Upload className="h-5 w-5" />
                <span className="text-xs">Yüklemek için bırakın</span>
              </>
            )}
          </div>
        )}
      </div>
      <div className="flex gap-2">
        <Input
          value={value}
          placeholder="Dosya URL'i (MinIO)"
          onChange={(e) => onChange(e.target.value)}
          className="flex-1"
        />
        <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)}>
          <FileText className="h-4 w-4" />
          Seç
        </Button>
        {value && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onChange("")}
            className="text-destructive hover:text-destructive"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
      <MediaPicker
        defaultFolder="content"
        open={open}
        onClose={() => setOpen(false)}
        onSelect={(url) => {
          onChange(url);
          setOpen(false);
        }}
      />
    </div>
  );
}

export function FieldRenderer({
  field,
  value,
  onChange,
}: {
  field: FieldDef;
  value: string;
  onChange: (v: string) => void;
}) {
  switch (field.type) {
    case "textarea":
      return <Textarea rows={3} value={value} onChange={(e) => onChange(e.target.value)} />;
    case "richtext":
      return <RichTextEditor value={value} onChange={onChange} />;
    case "image":
      return <ImageField value={value} onChange={onChange} />;
    case "file":
      return <FileField value={value} onChange={onChange} />;
    case "text":
    default:
      return <Input value={value} onChange={(e) => onChange(e.target.value)} />;
  }
}
